import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';

// ── Forms ────────────────────────────────────────────────────────────────────

export type FieldType = 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox' | 'number' | 'date' | 'hidden';

export type FormField = {
  id: string;
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  options?: string[];
  placeholder?: string | null;
  defaultValue?: string | null;
  width: 'full' | 'half';
};

export type Popup = {
  enabled: boolean;
  trigger: 'delay' | 'scroll' | 'exit';
  delaySeconds: number;
  scrollPercent: number;
  pages: string[];
  frequencyDays: number;
  position: 'center' | 'bottom-right' | 'bottom-left';
};

export type FormSettings = {
  title?: string | null;
  description?: string | null;
  submitLabel: string;
  successMessage: string;
  redirectUrl?: string | null;
  downloadUrl?: string | null;
  unsorted: boolean;
  ownerId?: string | null;
  notifyUserIds: string[];
  matchContacts: boolean;
  consentText?: string | null;
  requireConsent: boolean;
  campaign?: string | null;
  colour: string;
  popup: Popup;
};

export type MarketingForm = {
  id: string;
  publicKey: string;
  name: string;
  fields: FormField[];
  settings: FormSettings;
  isActive: boolean;
  viewCount: number;
  submissionCount: number;
  conversionRate: number;
  url: string;
  embedCode: string;
  updatedAt: string;
};

export type PublicForm = {
  key: string;
  name: string;
  company: string;
  fields: FormField[];
  settings: Pick<FormSettings, 'title' | 'description' | 'submitLabel' | 'consentText' | 'requireConsent' | 'colour'>;
};

export function useForms() {
  return useQuery({ queryKey: ['growth', 'forms'], queryFn: () => apiGet<MarketingForm[]>('/growth/forms') });
}

export function useFormSubmissions(id: string | null) {
  return useQuery({
    queryKey: ['growth', 'forms', id, 'submissions'],
    queryFn: () =>
      apiGet<{ id: string; data: Record<string, unknown>; pageUrl: string | null; utm: Record<string, string> | null; leadId: string | null; contactId: string | null; createdAt: string }[]>(
        `/growth/forms/${id}/submissions`,
      ),
    enabled: Boolean(id),
  });
}

export function useSaveForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string; name: string; fields: FormField[]; settings: FormSettings; isActive: boolean }) =>
      id ? apiPatch<MarketingForm>(`/growth/forms/${id}`, body) : apiPost<MarketingForm>('/growth/forms', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['growth', 'forms'] }),
  });
}

export function useDeleteForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/growth/forms/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['growth', 'forms'] }),
  });
}

// ── Landing pages ────────────────────────────────────────────────────────────

export type BlockType = 'hero' | 'text' | 'image' | 'features' | 'form' | 'cta' | 'testimonials' | 'faq' | 'stats' | 'logos' | 'video';
export type Block = { id: string; type: BlockType; [key: string]: unknown };
export type PageTheme = { primary: string; background: 'light' | 'dark' | 'aurora' };

export type LandingPage = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  blocks: Block[];
  theme: PageTheme | null;
  status: 'DRAFT' | 'PUBLISHED';
  publishedAt: string | null;
  viewCount: number;
  conversions: number;
  conversionRate: number;
  url: string;
  updatedAt: string;
};

export type PublicPage = {
  id: string;
  title: string;
  description: string | null;
  blocks: Block[];
  theme: PageTheme | null;
  company: string;
  trackingKey: string;
  forms: Record<string, PublicForm | null>;
};

export type Asset = { id: string; name: string; url: string; size: number; mimeType: string };

export function usePages() {
  return useQuery({ queryKey: ['growth', 'pages'], queryFn: () => apiGet<LandingPage[]>('/growth/pages') });
}

export function usePage(id: string | undefined) {
  return useQuery({ queryKey: ['growth', 'pages', id], queryFn: () => apiGet<LandingPage>(`/growth/pages/${id}`), enabled: Boolean(id) });
}

export function useSavePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string; title: string; slug?: string; description?: string | null; blocks: Block[]; theme: PageTheme }) =>
      id ? apiPatch<LandingPage>(`/growth/pages/${id}`, body) : apiPost<LandingPage>('/growth/pages', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['growth', 'pages'] }),
  });
}

export function usePublishPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) => apiPost<LandingPage>(`/growth/pages/${id}/publish`, { published }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['growth', 'pages'] }),
  });
}

export function usePageActions() {
  const queryClient = useQueryClient();
  const done = () => void queryClient.invalidateQueries({ queryKey: ['growth', 'pages'] });
  return {
    duplicate: useMutation({ mutationFn: (id: string) => apiPost<LandingPage>(`/growth/pages/${id}/duplicate`), onSuccess: done }),
    remove: useMutation({ mutationFn: (id: string) => apiDelete(`/growth/pages/${id}`), onSuccess: done }),
  };
}

export function useAssets(enabled = true) {
  return useQuery({ queryKey: ['growth', 'assets'], queryFn: () => apiGet<Asset[]>('/growth/pages/assets'), enabled });
}

export function useUploadAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);
      const response = await apiClient.post<{ data: Asset }>('/growth/pages/assets', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['growth', 'assets'] }),
  });
}

// ── Site & analytics ─────────────────────────────────────────────────────────

export type SiteSettings = {
  publicKey: string;
  domains: string[];
  helpCenterSlug: string;
  helpCenterName: string;
  helpCenterColour: string;
  helpCenterEnabled: boolean;
  snippet: string;
  helpCenterUrl: string;
};

export function useSite(enabled = true) {
  return useQuery({ queryKey: ['growth', 'site'], queryFn: () => apiGet<SiteSettings>('/growth/site'), enabled });
}

export function useSaveSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Pick<SiteSettings, 'domains' | 'helpCenterSlug' | 'helpCenterName' | 'helpCenterColour' | 'helpCenterEnabled'>) =>
      apiClient.put('/growth/site', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['growth', 'site'] }),
  });
}

export type Analytics = {
  days: number;
  visitors: number;
  views: number;
  submissions: number;
  conversionRate: number;
  daily: { day: string; views: number; visitors: number }[];
  pages: { path: string; views: number; visitors: number }[];
  sources: { source: string; medium: string | null; visitors: number; leads: number }[];
  campaigns: { campaign: string; views: number; visitors: number }[];
};

export function useAnalytics(days: number) {
  return useQuery({ queryKey: ['growth', 'analytics', days], queryFn: () => apiGet<Analytics>('/growth/site/analytics', { params: { days } }) });
}

export function useAttribution(days: number) {
  return useQuery({
    queryKey: ['growth', 'attribution', days],
    queryFn: () =>
      apiGet<{ currency: string; rows: { source: string; campaign: string | null; leads: number; converted: number; won: number; revenue: string }[] }>(
        '/growth/site/attribution',
        { params: { days } },
      ),
  });
}

export function useRecordVisits(entityType: string, entityId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['growth', 'visits', entityType, entityId],
    queryFn: () =>
      apiGet<{
        visitors: { id: string; firstUtm: Record<string, string> | null; firstReferrer: string | null; firstSeenAt: string; pageViews: number }[];
        views: { id: string; url: string; path: string; title: string | null; createdAt: string }[];
      }>('/growth/site/visits', { params: { entityType, entityId } }),
    enabled,
  });
}

// ── Knowledge base ───────────────────────────────────────────────────────────

export type KbCategory = { id: string; name: string; slug: string; description: string | null; icon: string | null; displayOrder: number; articleCount: number };
export type KbArticleRow = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  status: 'DRAFT' | 'PUBLISHED';
  isInternal: boolean;
  viewCount: number;
  helpfulYes: number;
  helpfulNo: number;
  categoryId: string | null;
  category: { id: string; name: string } | null;
  updatedAt: string;
};
export type KbArticle = KbArticleRow & { body: string; url: string | null };

export function useKbCategories() {
  return useQuery({ queryKey: ['kb', 'categories'], queryFn: () => apiGet<KbCategory[]>('/kb/categories') });
}

export function useKbArticles(params: { q?: string; categoryId?: string }) {
  return useQuery({
    queryKey: ['kb', 'articles', params],
    queryFn: () => apiGet<KbArticleRow[]>('/kb/articles', { params: Object.fromEntries(Object.entries(params).filter(([, value]) => value)) }),
  });
}

export function useKbArticle(id: string | null) {
  return useQuery({ queryKey: ['kb', 'article', id], queryFn: () => apiGet<KbArticle>(`/kb/articles/${id}`), enabled: Boolean(id) });
}

export function useKbMutations() {
  const queryClient = useQueryClient();
  const done = () => void queryClient.invalidateQueries({ queryKey: ['kb'] });
  return {
    saveCategory: useMutation({
      mutationFn: ({ id, ...body }: { id?: string; name: string; description?: string | null; icon?: string | null; displayOrder: number }) =>
        id ? apiPatch(`/kb/categories/${id}`, body) : apiPost('/kb/categories', body),
      onSuccess: done,
    }),
    removeCategory: useMutation({ mutationFn: (id: string) => apiDelete(`/kb/categories/${id}`), onSuccess: done }),
    saveArticle: useMutation({
      mutationFn: ({ id, ...body }: { id?: string; title: string; slug?: string; categoryId?: string | null; excerpt?: string | null; body: string; status: 'DRAFT' | 'PUBLISHED'; isInternal: boolean }) =>
        id ? apiPatch<KbArticle>(`/kb/articles/${id}`, body) : apiPost<KbArticle>('/kb/articles', body),
      onSuccess: done,
    }),
    removeArticle: useMutation({ mutationFn: (id: string) => apiDelete(`/kb/articles/${id}`), onSuccess: done }),
  };
}

// ── Ads and social ───────────────────────────────────────────────────────────

export type AdSource = {
  id: string;
  platform: 'google' | 'meta' | 'tiktok';
  name: string;
  channelId: string | null;
  ownerId: string | null;
  unsorted: boolean;
  isActive: boolean;
  leadCount: number;
  lastLeadAt: string | null;
  lastError: string | null;
  webhookUrl: string | null;
  key: string | null;
};

export function useAdSources() {
  return useQuery({ queryKey: ['growth', 'ads'], queryFn: () => apiGet<AdSource[]>('/growth/ads') });
}

export function useAdMutations() {
  const queryClient = useQueryClient();
  const done = () => void queryClient.invalidateQueries({ queryKey: ['growth', 'ads'] });
  return {
    save: useMutation({
      mutationFn: ({ id, ...body }: { id?: string; platform: AdSource['platform']; name: string; channelId?: string | null; ownerId?: string | null; unsorted: boolean; isActive: boolean }) =>
        id ? apiPatch<AdSource>(`/growth/ads/${id}`, body) : apiPost<AdSource>('/growth/ads', body),
      onSuccess: done,
    }),
    remove: useMutation({ mutationFn: (id: string) => apiDelete(`/growth/ads/${id}`), onSuccess: done }),
  };
}

export type SocialAccount = { id: string; kind: 'FACEBOOK' | 'INSTAGRAM'; name: string };
export type SocialPost = {
  id: string;
  channelIds: string[];
  message: string;
  imageUrl: string | null;
  linkUrl: string | null;
  scheduledAt: string | null;
  status: 'DRAFT' | 'SCHEDULED' | 'PUBLISHING' | 'PUBLISHED' | 'PARTIAL' | 'FAILED';
  results: { channelId: string; name: string; postId: string | null; url: string | null; error: string | null }[] | null;
  publishedAt: string | null;
  createdAt: string;
};

export function useSocial() {
  return useQuery({ queryKey: ['growth', 'social'], queryFn: () => apiGet<SocialPost[]>('/growth/social'), refetchInterval: 30_000 });
}

export function useSocialAccounts() {
  return useQuery({ queryKey: ['growth', 'social', 'accounts'], queryFn: () => apiGet<SocialAccount[]>('/growth/social/accounts') });
}

export function useSocialMutations() {
  const queryClient = useQueryClient();
  const done = () => void queryClient.invalidateQueries({ queryKey: ['growth', 'social'] });
  return {
    save: useMutation({
      mutationFn: ({ id, ...body }: { id?: string; channelIds: string[]; message: string; imageUrl?: string | null; linkUrl?: string | null; scheduledAt?: string | null; draft: boolean }) =>
        id ? apiPatch<SocialPost>(`/growth/social/${id}`, body) : apiPost<SocialPost>('/growth/social', body),
      onSuccess: done,
    }),
    remove: useMutation({ mutationFn: (id: string) => apiDelete(`/growth/social/${id}`), onSuccess: done }),
  };
}
